import React, { useContext, useEffect, useState } from 'react';
import { t } from 'i18next';
import SelectInput, {
  SelectInputHelper
} from '../../common/SelectInput/SelectInput';
import {
  DatalistOption,
  DealerHomepageOptions,
  DropdownData,
  InsuranceJourneyContext,
  KeyValuePair,
  SelectOpt
} from '../../common/CommonTypes';
import TextInput from '../../common/TextInput/TextInput';
import { CommonValidation } from '../../common/CommonValidation/commonValidation';
import { ButtonContinue } from '../../common/Button/ButtonContinue';
import GenderSelection from '../../common/GenderSelection/GenderSelection';
import DateSelector from '../../common/DateSelector/DateSelector';
import { SiteContext } from '../../../context/SiteContext';
import { CarInsuranceApi } from '../../../api/CarInsuranceApi';
import { Modal, ModalBody, ModalFooter, ModalHeader } from 'reactstrap';
import { useLocation, useNavigate, useOutletContext } from 'react-router-dom';
import {
  populateDatalist,
  setCountryCode,
  getInsuranceJourneyMinMaxDob,
  getMonthNames,
  isValidName
} from '../../common/HelperMethods';
import Checkbox from '../../common/Checkbox/Checkbox';
import SearchTextInput from '../../common/SerachTextInput/SearchTextInput';
import RadioOptions from '../../common/RadioOptions/RadioOptions';
import { ButtonCommand } from '../../common/Button/ButtonCommand';
import {
  InsuranceJourneyHelperText,
  PageAbsolutePath,
  DefaultRadioOptions,
  ValidationMessages,
  ValidationConstants,
  InsuranceSaveForLaterMessage
} from '../../../consts/Common';
import ClickableHelpText from '../../common/HelpText/ClickableHelpText';
import { ButtonBack } from '../../common/Button/ButtonBack';
import useCarInsuranceController from '../../../hooks/insuranceJourney/useCarInsuranceController';
import {
  EmploymentStatus,
  Gender,
  PagesWithDropdown,
  Source,
  Title,
  Vendor
} from '../../common/Constants';
import LoadingContainer from '../../common/LoadingContainer/LoadingContainer';
import { useErrorBoundary } from 'react-error-boundary';

export type PersonalDetailErrors = {
  licenceType: string;
  dateOfBirth: string;
  title: string;
  firstName: string;
  lastName: string;
  email: string;
  selectedGender: string;
  maritalStatus: string;
  mobilePhone: string;
  yearsLicenceHeld: string;
  employmentStatus: string;
  occupation: string;
  industry: string;
  residentSinceDate: string;
  ownHome: string;
  expiryMonth: string;
};

const initialErrors = {
  dateOfBirth: '',
  title: '',
  lastName: '',
  firstName: '',
  email: '',
  selectedGender: '',
  maritalStatus: '',
  mobilePhone: '',
  licenceType: '',
  yearsLicenceHeld: '',
  employmentStatus: '',
  occupation: '',
  industry: '',
  residentSinceDate: '',
  ownHome: '',
  expiryMonth: ''
};

type LocationState = {
  dealer: string;
  dealerHomeOptions: DealerHomepageOptions;
};

const AboutYouPage = (): JSX.Element => {
  const { site } = useContext(SiteContext);

  const navigate = useNavigate();
  const { state } = useLocation();

  const { journey, pageEvents } = useOutletContext<InsuranceJourneyContext>();

  const { source, savingQuote, personalDetails, staticData, pageLoading } =
    journey;
  const { personalDetailsPage, commonEvents } = pageEvents;
  const {
    setCustomerSelfRegistration,
    setSavingQuote,
    onPageChange,
    setStaticData,
    setPageLoading
  } = commonEvents;

  const {
    onChangeGenderSelection,
    onChangeTitle,
    onChangeFirstName,
    onChangeLastName,
    onChangeEmailAddress,
    onChangeMaritalStatus,
    onChangeHomePhone,
    onChangeMobilePhone,
    onChangeDateOfBirth,
    onChangeEmploymentStatus,
    onChangeOccupation,
    onChangeIndustry,
    onChangeResidentSinceBirth,
    onChangeResidentSinceDate,
    onChangeOwnHome,
    onChangeExpiryMonth
  } = personalDetailsPage;

  const {
    title,
    gender,
    firstName,
    lastName,
    email,
    homePhone,
    maritalStatus,
    mobilePhone,
    dateOfBirth,
    employmentStatus,
    industry,
    industryKey,
    occupation,
    occupationKey,
    residentSinceBirth,
    residentSinceDate,
    ownHome,
    expiryMonth
  } = personalDetails;

  const [dobIsValid, setDobIsValid] = useState(true);
  const [updateDobError, setUpdateDobError] = useState(false);
  const [updateResidentSinceDateError, setUpdateResidentSinceDateError] =
    useState(false);

  const token = site.user.token;

  const [errors, setErrors] = useState<PersonalDetailErrors>(initialErrors);

  const { showBoundary } = useErrorBoundary();
  const { getAboutYouPageDropdownValues } = useCarInsuranceController();

  const [occupationTypes, setOccupationTypes] = useState<DatalistOption[]>([]);

  const [industryTypes, setIndustryTypes] = useState<DatalistOption[]>([]);

  const today = new Date();
  const { minDob, maxDob } = getInsuranceJourneyMinMaxDob();
  const monthNames = getMonthNames();
  const [showPopup, setShowPopup] = useState(false);
  const [occupationReadOnly, setOccupationReadOnly] = useState(false);
  const [industryReadOnly, setIndustryReadOnly] = useState(false);

  const [titleOptions, setTitleOptions] = useState<SelectOpt[]>([]);
  const [genderOptions, setGenderOptions] = useState<SelectOpt[]>([]);
  const [maritalStatusOptions, setMaritalStatusOptions] = useState<SelectOpt[]>(
    []
  );
  const [employmentStatusOptions, setEmploymentStatusOptions] = useState<
    SelectOpt[]
  >([]);

  useEffect(() => {
    const titles = staticData?.find(
      (d) =>
        d.page === PagesWithDropdown.AboutYouPage && d.controlName === 'title'
    )?.values;

    const genders = staticData?.find(
      (d) =>
        d.page === PagesWithDropdown.AboutYouPage && d.controlName === 'genders'
    )?.values;

    const maritalStatusValues = staticData?.find(
      (d) =>
        d.page === PagesWithDropdown.AboutYouPage &&
        d.controlName === 'maritalStatus'
    )?.values;

    const employmentStatusValues = staticData?.find(
      (d) =>
        d.page === PagesWithDropdown.AboutYouPage &&
        d.controlName === 'employmentStatus'
    )?.values;

    titles ? setTitleOptions(titles) : setTitleOptions([]);
    genders ? setGenderOptions(genders) : setGenderOptions([]);
    maritalStatusValues
      ? setMaritalStatusOptions(maritalStatusValues)
      : setMaritalStatusOptions([]);
    employmentStatusValues
      ? setEmploymentStatusOptions(employmentStatusValues)
      : setEmploymentStatusOptions([]);
  }, [staticData]);

  const isAgeValid = (): boolean => {
    if (dateOfBirth === undefined) {
      return false;
    }

    const dob =
      typeof dateOfBirth === 'string' ? new Date(dateOfBirth) : dateOfBirth;

    let age = today.getFullYear() - dob.getFullYear();

    const monthDiff = today.getMonth() - dob.getMonth();

    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < dob.getDate())) {
      age--;
    }

    const result = age >= 17 && age <= 90;

    return result;
  };

  const lastNameRequiredMsg = t(
    'personal-details.lastname-required-validation'
  );

  const lastNameLimitError = t('personal-details.lastname-minchar-validation');
  const invalidEmailMsg = t(
    'personal-details.email-invalid-personal-details-validation'
  );
  const titleErrorMsg = 'Title is required';
  const genderErrorMsg = 'Gender is required';

  const emailRequiredMsg = t('personal-details.email-required-validation');
  const dobRequiredMsg = t('personal-details.dob-required-validation');
  const dobInvalidMsg = t('personal-details.dob-invalid-validation');
  const ageOutOfRange = 'Customer age must be between 17 to 90';
  const maritalStatusRequiredMsg = 'Marital status is required';
  const expiryMonthRequiredMsg =
    'Existing insurance policy expiry month is required';
  const mobilePhoneRequiredMsg = 'Mobile phone is required';
  const mobilePhoneInvalidMsg = 'Mobile phone is not valid';

  const occupationErrorMessage =
    "Sorry, we couldn't find that occupation. Please re-check your spelling, or try a different job title which closely matches yours.";
  const industryErrorMessage =
    "Sorry, we couldn't find that industry. Please re-check your spelling, or try a different industry which closely matches yours.";

  const validatePersonalDetails = (): boolean => {
    let isValid = true;

    const error: PersonalDetailErrors = {
      ...initialErrors
    };

    if (title === SelectInputHelper.NullValue()) {
      isValid = false;
      error.title = titleErrorMsg;
    }

    if (CommonValidation.isEmpty(gender)) {
      isValid = false;
      error.selectedGender = genderErrorMsg;
    }

    if (CommonValidation.isEmpty(lastName)) {
      isValid = false;
      error.lastName = lastNameRequiredMsg;
    } else if (
      lastName.length < ValidationConstants.Insurance.lastNameMinCharLimit
    ) {
      isValid = false;
      error.lastName = lastNameLimitError;
    } else {
      if (isValidName(lastName)) error.lastName = '';
      else {
        error.lastName = ValidationMessages.InsuranceJourney.errorInvalidName;
        isValid = false;
      }
    }

    if (CommonValidation.isEmpty(firstName)) {
      isValid = false;
      error.firstName =
        ValidationMessages.InsuranceJourney.firstnameRequiredMsg;
    } else if (
      !CommonValidation.isValidLength(
        firstName,
        ValidationConstants.Insurance.firstNameCharLimit
      )
    ) {
      isValid = false;
      error.firstName = ValidationMessages.InsuranceJourney.firstNameLimitError;
    } else {
      if (isValidName(firstName)) error.firstName = '';
      else {
        error.firstName = ValidationMessages.InsuranceJourney.errorInvalidName;
        isValid = false;
      }
    }

    if (
      !SelectInputHelper.isValidValue(maritalStatus) ||
      CommonValidation.isEmpty(maritalStatus)
    ) {
      isValid = false;
      error.maritalStatus = maritalStatusRequiredMsg;
    }
    if (
      !SelectInputHelper.isValidValue(expiryMonth) ||
      CommonValidation.isEmpty(expiryMonth)
    ) {
      isValid = false;
      error.expiryMonth = expiryMonthRequiredMsg;
    }

    if (CommonValidation.isEmpty(mobilePhone)) {
      error.mobilePhone = mobilePhoneRequiredMsg;
      isValid = false;
    } else if (mobilePhone.length < 11) {
      error.mobilePhone = mobilePhoneInvalidMsg;
      isValid = false;
    }

    if (CommonValidation.isNotEmpty(email)) {
      if (!CommonValidation.isValidEmail(email)) {
        error.email = invalidEmailMsg;
        isValid = false;
      }
    } else {
      isValid = false;
      error.email = emailRequiredMsg;
    }

    if (dobIsValid === false) {
      setUpdateDobError(true);
      isValid = false;
      error.dateOfBirth = dobInvalidMsg;
    } else if (CommonValidation.isEmpty(dateOfBirth)) {
      setUpdateDobError(true);
      isValid = false;
      error.dateOfBirth = dobRequiredMsg;
    } else if (isAgeValid() === false) {
      setUpdateDobError(true);
      isValid = false;
      error.dateOfBirth = ageOutOfRange;
    }

    if (!residentSinceBirth && CommonValidation.isEmpty(residentSinceDate)) {
      isValid = false;
      error.residentSinceDate = 'Resident since date is required';
      setUpdateResidentSinceDateError(true);
    } else if (
      !residentSinceBirth &&
      !CommonValidation.isValidDate(residentSinceDate, false)
    ) {
      isValid = false;
      error.residentSinceDate = 'Resident since date is invalid';
    }

    if (CommonValidation.isEmpty(occupationKey)) {
      error.occupation = 'Occupation is required ';
      isValid = false;
    }

    if (CommonValidation.isEmpty(industryKey)) {
      error.industry = 'Industry is required ';
      isValid = false;
    }

    if (ownHome === undefined || ownHome === null) {
      error.ownHome = 'This field is required ';
      isValid = false;
    }

    if (employmentStatus == SelectInputHelper.NullValue()) {
      error.employmentStatus = 'Employment status is required ';
      isValid = false;
    }

    setErrors(error);
    return isValid;
  };

  const onContinueClick = () => {
    const isValid = validatePersonalDetails();
    if (isValid) {
      const path: string = setCountryCode(
        PageAbsolutePath.Insurance.DrivingHistory,
        site.countryCode
      );
      navigate(path);
      onPageChange(PageAbsolutePath.Insurance.AboutYou, true);
    } else {
      console.log('Not Valid Page');
    }
  };

  useEffect(() => {
    if (title === Title.Mr) onChangeGenderSelection(Gender.Male);
    if (title === Title.Miss || title === Title.Mrs || title === Title.Ms)
      onChangeGenderSelection(Gender.Female);
  }, [title]);

  useEffect(() => {
    populateOccupation();
  }, [employmentStatus]);

  useEffect(() => {
    populateIndustry();
  }, [occupation]);

  useEffect(() => {
    window.scrollTo(0, 0);

    if (state) {
      const { dealer, dealerHomeOptions } = state as LocationState;
      setCustomerSelfRegistration(
        Vendor.FranchiseRetailer,
        dealer,
        dealerHomeOptions
      );
    } else {
      const dropDownValuesForPage = staticData?.find(
        (d) => d.page === PagesWithDropdown.AboutYouPage
      );

      !dropDownValuesForPage &&
        getAboutYouPageDropdownValues(
          token,
          getDropdownValuesForPageSuccess,
          onBackendFailure
        );
    }
  }, []);

  useEffect(() => {
    if (source === Source.CustomerSelfRegistration) {
      const dropDownValuesForPage = staticData?.find(
        (d) => d.page === PagesWithDropdown.AboutYouPage
      );

      !dropDownValuesForPage &&
        getAboutYouPageDropdownValues(
          token,
          getDropdownValuesForPageSuccess,
          onBackendFailure
        );
    }
  }, [source]);

  useEffect(() => {
    staticData &&
      setStaticData(
        staticData.filter(
          (d) =>
            !(
              d.page === PagesWithDropdown.DrivingHistoryPage &&
              d.controlName === 'years-licence-held'
            )
        )
      );
  }, [dateOfBirth]);

  const getDropdownValuesForPageSuccess = (aboutYouList: DropdownData[]) => {
    setStaticData(
      staticData ? staticData.concat(...aboutYouList) : aboutYouList
    );
  };

  const onBackendFailure = (err: string) => {
    showBoundary(new Error(err));
  };

  const onDobValidated = (isValid: boolean): void => {
    setUpdateDobError(false);
    setDobIsValid(isValid);
  };

  const populateOccupation = (): void => {
    switch (employmentStatus) {
      case EmploymentStatus.Retired: {
        onChangeOccupation('Retired', 'R09');
        setOccupationReadOnly(true);
        break;
      }
      case EmploymentStatus.IndependentMeans: {
        onChangeOccupation('Independent Means', 'I02');
        setOccupationReadOnly(true);
        break;
      }
      case EmploymentStatus.NotEmployedDueToDisability: {
        onChangeOccupation('Not Employed Due To Disability', '43D');
        setOccupationReadOnly(true);
        break;
      }
      case EmploymentStatus.Unemployed: {
        onChangeOccupation('Unemployed', 'U03');
        setOccupationReadOnly(true);
        break;
      }
      case EmploymentStatus.HouseholdDuties: {
        if (gender === Gender.Male) {
          onChangeOccupation('Househusband', '163');
        }
        if (gender === Gender.Female) {
          onChangeOccupation('Housewife', 'H09');
        }
        setOccupationReadOnly(true);
        break;
      }
      default: {
        onChangeOccupation('', '');
        setOccupationReadOnly(false);
      }
    }
  };

  const populateIndustry = (): void => {
    switch (occupation?.toLowerCase()) {
      case 'retired': {
        onChangeIndustry('None - Retired', '947');
        setIndustryReadOnly(true);
        break;
      }
      case 'independent means':
      case 'unemployed': {
        onChangeIndustry('None - Unemployed', '747');
        setIndustryReadOnly(true);
        break;
      }
      case 'not employed due to disability': {
        onChangeIndustry('None - Not Employed Due to Disability', '949');
        setIndustryReadOnly(true);
        break;
      }
      case 'househusband':
      case 'housewife': {
        onChangeIndustry('Not in Employment', '186');
        setIndustryReadOnly(true);
        break;
      }
      default:
        if (!occupation) {
          onChangeIndustry('', '');
        } else {
          onChangeIndustry(industry, industryKey);
        }
        setIndustryReadOnly(false);
    }
  };

  const onToggleCheckbox = () => {
    onChangeResidentSinceBirth(!residentSinceBirth);
  };

  const handleOnLeaveOccupationField = (
    e: React.FormEvent<HTMLInputElement>
  ) => {
    getOccupationsBySearch(
      e.currentTarget.value,
      validateOccupationSuccess,
      onBackendFailure
    );
  };

  const handleOnLeaveIndustryField = (e: React.FormEvent<HTMLInputElement>) => {
    getIndustriesBySearch(
      e.currentTarget.value,
      validateIndustrySuccess,
      onBackendFailure
    );
  };

  const handleOccupationChange = (e: React.FormEvent<HTMLInputElement>) => {
    errors.occupation && setErrors({ ...errors, occupation: '' });
    const newOccupationKey = occupationTypes.find(
      (opt) => opt.text === e.currentTarget.value
    )?.value;
    onChangeOccupation(e.currentTarget.value, newOccupationKey);
    loadOccupationsBySearch(e.currentTarget.value);
  };

  const loadOccupationsBySearch = (value: string) => {
    if (value.length > 2)
      getOccupationsBySearch(value, setOccupationListSuccess, onBackendFailure);
    else setOccupationTypes(populateDatalist([]));
  };

  const validateOccupationSuccess = (occupations: KeyValuePair[]) => {
    const isValidOccupation =
      occupations.filter((o) => o.value === occupation).length > 0;

    if (isValidOccupation) setErrors({ ...errors, occupation: '' });
    else {
      setErrors({ ...errors, occupation: occupationErrorMessage });
      onChangeOccupation('');
      setOccupationTypes(populateDatalist([]));
    }
  };

  const validateIndustrySuccess = (industries: KeyValuePair[]) => {
    const isValidIndustry =
      industries.filter((i) => i.value === industry).length > 0;

    if (isValidIndustry) setErrors({ ...errors, industry: '' });
    else {
      setErrors({ ...errors, industry: industryErrorMessage });
      onChangeIndustry('');
      setIndustryTypes(populateDatalist([]));
    }
  };

  const setOccupationListSuccess = (occupations: KeyValuePair[]) => {
    setOccupationTypes(populateDatalist(occupations));
  };

  const getOccupationsBySearch = async (
    searchText: string,
    successFunction: (occupations: KeyValuePair[]) => void,
    failureFunction: (err: string) => void
  ) => {
    const controller = new AbortController();

    await new CarInsuranceApi().GetOccupationsBySearchAsync(
      searchText,
      token,
      successFunction,
      failureFunction,
      controller.signal
    );

    return () => {
      controller.abort();
    };
  };

  const handleIndustryChange = (e: React.FormEvent<HTMLInputElement>) => {
    errors.industry && setErrors({ ...errors, industry: '' });
    const newIndustryKey = industryTypes.find(
      (opt) => opt.text === e.currentTarget.value
    )?.value;
    onChangeIndustry(e.currentTarget.value, newIndustryKey);
    loadIndustriesBySearch(e.currentTarget.value);
  };

  const loadIndustriesBySearch = (value: string) => {
    if (value.length > 2)
      getIndustriesBySearch(value, setIndustryListSuccess, onBackendFailure);
    else setIndustryTypes(populateDatalist([]));
  };

  const setIndustryListSuccess = (industries: KeyValuePair[]) => {
    setIndustryTypes(populateDatalist(industries));
  };

  const getIndustriesBySearch = async (
    searchText: string,
    successFunction: (industries: KeyValuePair[]) => void,
    failureFunction: (err: string) => void
  ) => {
    const controller = new AbortController();

    await new CarInsuranceApi().GetIndustriesBySearchAsync(
      searchText,
      token,
      successFunction,
      failureFunction,
      controller.signal
    );

    return () => {
      controller.abort();
    };
  };

  const onBackClick = (): void => {
    navigate(-1);
  };

  const onSaveLaterClick = (): void => {
    setPageLoading(true);
    setSavingQuote(true);
  };

  return (
    <LoadingContainer
      loading={pageLoading}
      message={InsuranceSaveForLaterMessage}>
      <>
        <div>
          <div>
            <div className='one-col'>
              <ButtonCommand
                name='btn-back-top'
                text={'< Back'}
                onClick={onBackClick}
              />
            </div>
            <h4 className='section-header m1-top'>Your details</h4>
          </div>
          <div className='table-flex-layout'>
            <div className='form-container'>
              <div className='two-col'>
                <div>
                  <div className='control-label'>
                    {t('personal-details.title-label')}
                  </div>
                  <div className='control'>
                    <SelectInput
                      name='title'
                      className='input-text w250'
                      value={title}
                      options={titleOptions}
                      onChange={onChangeTitle}
                      addDefaultOption
                      forceDefault
                      error={errors.title}
                    />
                  </div>
                </div>
              </div>
              <div className='two-col'>
                <div>
                  <div className='max-content'>
                    <GenderSelection
                      genderSelectionError={errors.selectedGender}
                      selectedGender={gender}
                      genderList={genderOptions}
                      setSelectedGender={onChangeGenderSelection}
                    />
                  </div>
                </div>
              </div>
              <div className='two-col'>
                <div className='control-label'>
                  {t('personal-details.first-name-label')}
                </div>
                <div className='control'>
                  <TextInput
                    placeholder={t('personal-details.first-name-label')}
                    name='firstname'
                    className='input-text w250'
                    value={firstName}
                    onChange={(e: React.FormEvent<HTMLInputElement>) =>
                      onChangeFirstName(e)
                    }
                    error={errors.firstName}
                    readOnly={false}
                  />
                </div>
              </div>
              <div className='two-col'>
                <div className='control-label'>
                  {t('personal-details.lastname-label')}
                </div>
                <div className='control'>
                  <TextInput
                    placeholder={t('personal-details.lastname-label')}
                    name='lastname'
                    className='input-text w250'
                    value={lastName}
                    onChange={(e: React.FormEvent<HTMLInputElement>) =>
                      onChangeLastName(e)
                    }
                    error={errors.lastName}
                    readOnly={false}
                  />
                </div>
              </div>
              <div className='two-col'>
                <div className='control-label'>
                  {t('personal-details.date-of-birth-label')}
                </div>
                <div className='control'>
                  <DateSelector
                    value={dateOfBirth}
                    minDate={minDob}
                    maxDate={maxDob}
                    invalidDateMessage={errors.dateOfBirth}
                    updateDobError={updateDobError}
                    culture={site.cultureCode}
                    onChange={(value) => onChangeDateOfBirth(value)}
                    onValidated={(isValid) => onDobValidated(isValid)}
                    name={'dob'}
                  />
                </div>
              </div>
              <div className='two-col'>
                <div className='control-label'>
                  {t('personal-details.email-address-label')}
                </div>
                <div className='control'>
                  <TextInput
                    placeholder={t('personal-details.email-address-label')}
                    name='email'
                    className='input-text w250'
                    value={email}
                    onChange={(e: React.FormEvent<HTMLInputElement>) =>
                      onChangeEmailAddress(e)
                    }
                    error={errors.email}
                    readOnly={false}
                  />
                </div>
              </div>
              <div className='two-col'>
                <div className='control-label'>
                  {t('personal-details.home-phone')}
                </div>
                <div className='control'>
                  <TextInput
                    placeholder={t('personal-details.home-phone')}
                    name='homePhone'
                    className='input-text w250'
                    maxLength={11}
                    value={homePhone}
                    onChange={(e: React.FormEvent<HTMLInputElement>) =>
                      onChangeHomePhone(e)
                    }
                    readOnly={false}
                  />
                </div>
              </div>
              <div className='two-col'>
                <div className='control-label help-text'>
                  {t('personal-details.mobile-phone')}
                  <ClickableHelpText
                    helpText={
                      InsuranceJourneyHelperText.Pages.AboutYou.MobilePhone
                    }
                  />
                </div>
                <div className='control'>
                  <TextInput
                    placeholder={t('personal-details.mobile-phone')}
                    name='mobilePhone'
                    className='input-text w250'
                    maxLength={11}
                    value={personalDetails.mobilePhone}
                    onChange={(e: React.FormEvent<HTMLInputElement>) =>
                      onChangeMobilePhone(e)
                    }
                    error={errors.mobilePhone}
                    readOnly={false}
                  />
                </div>
              </div>
              <div className='two-col'>
                <div className='control-label'>
                  {t('personal-details.marital-status')}
                </div>
                <div className='control'>
                  <SelectInput
                    name='maritalStatus'
                    className='input-text w250'
                    value={maritalStatus}
                    onChange={onChangeMaritalStatus}
                    options={maritalStatusOptions}
                    forceDefault
                    addDefaultOption
                    error={errors.maritalStatus}
                  />
                </div>
              </div>
              <div className='two-col'>
                <div className='control-label'>
                  Existing Insurance policy expiry month
                </div>
                <div className='control'>
                  <SelectInput
                    name='expiryMonth'
                    className='input-text w250'
                    value={expiryMonth}
                    onChange={onChangeExpiryMonth}
                    options={monthNames}
                    forceDefault
                    addDefaultOption
                    error={errors.expiryMonth}
                  />
                </div>
              </div>
            </div>
            <div>
              <div className='flex-column'>
                <h4 className='section-header m3-top'>Residency Details</h4>
              </div>
            </div>
            <div className='form-container'>
              <div className='one-col'>
                <div className='control-label'>
                  How Long Have You Been Resident In The UK?
                </div>
                <div className='control'>
                  <Checkbox
                    id='resident-since-birth'
                    checked={residentSinceBirth}
                    text={'Since birth'}
                    onChange={onToggleCheckbox}
                    disabled={false}
                  />
                  <div style={{ paddingTop: '10px', paddingBottom: '3px' }}>
                    or since
                  </div>
                  <DateSelector
                    value={residentSinceDate}
                    minDate={minDob}
                    maxDate={today}
                    invalidDateMessage={errors.residentSinceDate}
                    updateDobError={updateResidentSinceDateError}
                    culture={site.cultureCode}
                    hideDay={true}
                    disabled={residentSinceBirth}
                    onChange={(value) => onChangeResidentSinceDate(value)}
                    onValidated={() => {}}
                    name={'resident-since'}
                  />
                </div>
              </div>
              <div className='one-col'>
                <div className='control-label'>Employment Status</div>
                <div className='control'>
                  <SelectInput
                    name='employmentStatus'
                    className='input-text w250'
                    value={employmentStatus}
                    onChange={onChangeEmploymentStatus}
                    options={employmentStatusOptions}
                    forceDefault
                    addDefaultOption
                    error={errors.employmentStatus}
                  />
                </div>
              </div>
              <div className='two-col'>
                <div className='control-label'>What's Your Occupation?</div>
                <h6>
                  Start typing the first three letters of your job title then
                  select from the list. If you can't find your exact title
                  please choose whichever is closest
                </h6>
                <div className='control'>
                  <SearchTextInput
                    name='occupation'
                    className='input-text w250'
                    value={occupation}
                    onChange={handleOccupationChange}
                    onLeave={handleOnLeaveOccupationField}
                    readOnly={occupationReadOnly}
                    listName='occupationList'
                    listData={occupationTypes}
                    error={errors.occupation}
                  />
                </div>
              </div>
              <div className='two-col'>
                <div className='control-label'>
                  Which Industry Do You Work In?
                </div>
                <h6>
                  Start typing the first three letters of the industry you work
                  in then select from the list. If you can't find the exact one,
                  please choose whichever is closest
                </h6>
                <div className='control'>
                  <SearchTextInput
                    name='industry'
                    className='input-text w250'
                    value={industry}
                    onChange={handleIndustryChange}
                    onLeave={handleOnLeaveIndustryField}
                    readOnly={industryReadOnly}
                    listName='industryList'
                    listData={industryTypes}
                    error={errors.industry}
                  />
                </div>
              </div>
              <div className='one-col'>
                <div>
                  <div className='max-content'>
                    <RadioOptions
                      errorMessage={errors.ownHome}
                      selectedOption={
                        ownHome === null ? '' : ownHome ? 'Yes' : 'No'
                      }
                      optionsList={DefaultRadioOptions}
                      setSelectedOption={(value) =>
                        onChangeOwnHome(value === 'Yes')
                      }
                      label={'Do you own your home?'}
                      name='ownHomeQuestion'
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className='flex-row form-nav'>
            <ButtonBack
              name='btn-back'
              text='Back'
              onClick={onBackClick}
              additionalClassNames='btn-back'
            />
            <ButtonContinue
              name='btn-save-later'
              text='Save For Later'
              onClick={onSaveLaterClick}
              additionalClassNames='btn-save'
              disabled={savingQuote}
            />
            <ButtonContinue
              name='btn-continue'
              text='Next'
              onClick={onContinueClick}
              additionalClassNames='btn-continue'
            />
          </div>
        </div>
        <Modal
          role='modal-popup'
          isOpen={showPopup}
          className={`insurance-${site.brand.brandName}`}
          toggle={() => setShowPopup(false)}>
          <ModalHeader toggle={() => setShowPopup(false)}>
            We're Sorry
          </ModalHeader>
          <ModalBody>
            We cannot guarantee an automatic car under our standard courtesy car
            cover
          </ModalBody>
          <ModalFooter>
            <ButtonContinue
              name='modal-btn-continue'
              text={t('customer-product-page.button-quote-expired-continue')}
              onClick={() => setShowPopup(false)}
              additionalClassNames='btn-ok'
            />
          </ModalFooter>
        </Modal>
      </>
    </LoadingContainer>
  );
};

export default AboutYouPage;
